import apiClient from './client';

export type AppPlatform = 'ios' | 'android';

export interface AppVersionPolicy {
  platform: AppPlatform;
  latest_version: string;
  min_version: string;
  release_notes_uz: string;
  release_notes_ru: string;
  reminder_interval_days: number;
  store_url: string;
  updated_at: string;
}

export type UpdateAppVersionPayload = Omit<AppVersionPolicy, 'platform' | 'updated_at'>;

export const appVersionApi = {
  async list(): Promise<AppVersionPolicy[]> {
    const response = await apiClient.get<{ policies: AppVersionPolicy[] }>('/admin/app-version');
    return response.data.policies;
  },

  async update(platform: AppPlatform, payload: UpdateAppVersionPayload): Promise<AppVersionPolicy> {
    const response = await apiClient.put<AppVersionPolicy>(`/admin/app-version/${platform}`, payload);
    return response.data;
  },
};

// ── Validation (mirrors backend app-version.rules.ts) ──────────────────────

const SEMVER_RE = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?$/;

/** Compares only major.minor.patch (+ prerelease below release); null = invalid. */
export function compareVersions(a: string, b: string): number | null {
  const ma = SEMVER_RE.exec(a.trim());
  const mb = SEMVER_RE.exec(b.trim());
  if (!ma || !mb) return null;
  for (let i = 1; i <= 3; i++) {
    const d = Number(ma[i] ?? 0) - Number(mb[i] ?? 0);
    if (d !== 0) return Math.sign(d);
  }
  if (!ma[4] && !mb[4]) return 0;
  if (!ma[4]) return 1;
  if (!mb[4]) return -1;
  return ma[4] < mb[4] ? -1 : ma[4] > mb[4] ? 1 : 0;
}

export type PolicyErrors = Partial<Record<keyof UpdateAppVersionPayload, string>>;

export function validatePolicy(p: UpdateAppVersionPayload): PolicyErrors {
  const errors: PolicyErrors = {};
  if (!SEMVER_RE.test(p.latest_version.trim())) errors.latest_version = 'Format: 3.4.0';
  if (!SEMVER_RE.test(p.min_version.trim())) errors.min_version = 'Format: 3.4.0';
  if (!errors.latest_version && !errors.min_version && (compareVersions(p.min_version, p.latest_version) ?? 0) > 0) {
    errors.min_version = 'Minimal versiya Oxirgi versiyadan katta bo‘lishi mumkin emas';
  }
  const d = p.reminder_interval_days;
  if (!Number.isInteger(d) || d < 1 || d > 30) errors.reminder_interval_days = '1 dan 30 gacha butun son';
  try {
    if (new URL(p.store_url).protocol !== 'https:') errors.store_url = 'https:// bilan boshlanishi kerak';
  } catch {
    errors.store_url = 'To‘g‘ri URL kiriting (https://…)';
  }
  return errors;
}
